"use client";

import { motion } from "framer-motion";
import { ArrowRight, BookOpen, CornerDownRight, FileText } from "lucide-react";
import { WorkspaceFile } from "@/lib/workspace-context";

interface ConceptFlowMapProps {
  fileName: string;
  backlinks: WorkspaceFile[];
  headings: string[];
  outgoing: WorkspaceFile[];
  onNodeClick: (id: string) => void;
}

/**
 * Vista alternativa de "Mapa Conceptual de Flujo" que muestra:
 *  - Backlinks (entrada) a la izquierda
 *  - Documento activo con sus headings en el centro
 *  - Outgoing links (salida) a la derecha
 *
 * Sólo se renderiza cuando el modo del pane es "flow".
 */
export function ConceptFlowMap({
  fileName,
  backlinks,
  headings,
  outgoing,
  onNodeClick,
}: ConceptFlowMapProps) {
  return (
    <div className="flex-1 flex flex-col justify-center select-none py-4">
      <div className="mb-6 border-b border-border/10 pb-4 text-center">
        <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
          Mapa Conceptual de Flujo
        </span>
        <h2 className="text-lg font-bold text-foreground mt-0.5">
          Conexiones y Estructura de {fileName.replace(".md", "")}
        </h2>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
          Visualiza las fuentes de entrada, el flujo secuencial de secciones y el destino del conocimiento.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch relative">
        {/* 1. INPUT COLUMN (Origen) */}
        <div className="space-y-3 flex flex-col justify-center">
          <div className="text-center md:text-left mb-2">
            <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground/60">
              Documentos de Entrada
            </span>
          </div>
          {backlinks.map((f) => (
            <motion.div
              whileHover={{ scale: 1.02 }}
              key={f.id}
              onClick={() => onNodeClick(f.id)}
              className="p-3.5 rounded-2xl bg-secondary/10 hover:bg-secondary/35 border border-border/40 hover:border-primary/20 transition-all cursor-pointer flex items-center justify-between group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground/60 shrink-0" />
                <span className="text-xs font-semibold text-foreground/80 group-hover:text-primary transition-colors truncate">
                  {f.name.replace(".md", "")}
                </span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-primary transition-colors shrink-0" />
            </motion.div>
          ))}
          {backlinks.length === 0 && (
            <div className="p-4 rounded-xl border border-dashed border-border/30 text-center text-xs text-muted-foreground/40 italic">
              Sin entradas directas
            </div>
          )}
        </div>

        {/* 2. CENTER COLUMN (Estructura de la nota actual) */}
        <div className="flex flex-col items-center justify-center relative">
          <div className="w-full p-4 rounded-2xl bg-primary/5 border border-primary/20 shadow-lg relative z-10">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-primary/10">
              <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
                <BookOpen className="w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-foreground truncate">
                {fileName.replace(".md", "")}
              </span>
            </div>

            <div className="space-y-2 relative">
              {headings.map((heading, idx) => (
                <div key={idx} className="flex items-start gap-2 pl-2">
                  <CornerDownRight className="w-3 h-3 text-primary/70 shrink-0 mt-0.5" />
                  <span className="text-[11px] font-semibold text-foreground/80 leading-tight">
                    {heading}
                  </span>
                </div>
              ))}
              {headings.length === 0 && (
                <div className="text-[11px] text-muted-foreground/40 italic pl-2">
                  Sin encabezados definidos
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. OUTPUT COLUMN (Destino) */}
        <div className="space-y-3 flex flex-col justify-center">
          <div className="text-center md:text-right mb-2">
            <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground/60">
              Documentos de Salida
            </span>
          </div>
          {outgoing.map((f) => (
            <motion.div
              whileHover={{ scale: 1.02 }}
              key={f.id}
              onClick={() => onNodeClick(f.id)}
              className="p-3.5 rounded-2xl bg-secondary/10 hover:bg-secondary/35 border border-border/40 hover:border-primary/20 transition-all cursor-pointer flex items-center justify-between group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground/60 shrink-0" />
                <span className="text-xs font-semibold text-foreground/80 group-hover:text-primary transition-colors truncate">
                  {f.name.replace(".md", "")}
                </span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-primary transition-colors shrink-0" />
            </motion.div>
          ))}
          {outgoing.length === 0 && (
            <div className="p-4 rounded-xl border border-dashed border-border/30 text-center text-xs text-muted-foreground/40 italic">
              Sin enlaces de salida
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
